import { useTheme } from '../context/ThemeContext';
import { Building2, Tag, Users, Mail, Sparkles } from 'lucide-react';

const perks = [
  {
    icon: Tag,
    title: 'Custom pricing',
    description: 'Bulk voucher codes at rates that work for your team size.',
  },
  {
    icon: Users,
    title: 'Easy distribution',
    description: 'Give drivers Pro access instantly — no individual sign-ups or billing.',
  },
  {
    icon: Building2,
    title: 'Your brand',
    description: 'Vouchers carry your company branding inside the app.',
  },
  {
    icon: Sparkles,
    title: 'Premium value-add',
    description: 'Stand out from competitors by offering TripData Pro to your clients.',
  },
];

const Vouchers = () => {
  const theme = useTheme();

  return (
    <section
      id="vouchers"
      className="py-32 relative overflow-hidden"
      style={{ backgroundColor: theme.background }}
    >
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full blur-3xl opacity-10"
          style={{ background: `linear-gradient(135deg, ${theme.gradientStart}, ${theme.gradientEnd})` }}
        />
      </div>

      <div className="container-app relative z-10">

        {/* Header */}
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-medium"
            style={{ backgroundColor: theme.cardAlt, color: theme.textSecondary }}
          >
            <Building2 size={14} />
            For businesses &amp; fleet operators
          </div>

          <h2
            className="text-responsive-lg font-bold tracking-tight mb-6"
            style={{ color: theme.text }}
          >
            Give your team{' '}
            <span className="gradient-text">the edge.</span>
          </h2>

          <p
            className="text-xl max-w-2xl mx-auto"
            style={{ color: theme.textSecondary }}
          >
            Buy TripData Pro vouchers in bulk. Custom pricing, your branding,
            zero admin. The perfect value-add for dealers, insurers, and fleet managers.
          </p>
        </div>

        {/* 4-up perk cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {perks.map((perk, index) => (
            <div
              key={index}
              className="relative p-6 rounded-3xl overflow-hidden group"
              style={{
                backgroundColor: theme.card,
                border: `1px solid ${theme.border}`,
              }}
            >
              {/* Hover fill */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: `linear-gradient(135deg, ${theme.gradientStart}18, ${theme.gradientEnd}10)` }}
              />

              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 bg-gradient-brand"
                style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.25)' }}
              >
                <perk.icon className="w-6 h-6 text-white" />
              </div>
              <h3
                className="text-base font-semibold mb-2"
                style={{ color: theme.text }}
              >
                {perk.title}
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{ color: theme.textMuted }}
              >
                {perk.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA banner */}
        <div
          className="relative rounded-3xl p-10 md:p-14 overflow-hidden"
          style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}
        >
          {/* Blobs inside banner */}
          <div
            className="absolute -right-20 -top-20 w-72 h-72 rounded-full blur-3xl opacity-20 pointer-events-none"
            style={{ background: `linear-gradient(135deg, ${theme.gradientStart}, ${theme.gradientEnd})` }}
          />
          <div
            className="absolute -left-10 -bottom-10 w-48 h-48 rounded-full blur-3xl opacity-10 pointer-events-none"
            style={{ background: `linear-gradient(135deg, ${theme.gradientEnd}, ${theme.gradientStart})` }}
          />

          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h3
                className="text-2xl md:text-3xl font-bold tracking-tight mb-3"
                style={{ color: theme.text }}
              >
                Ready to offer TripData to your clients?
              </h3>
              <p style={{ color: theme.textSecondary }}>
                Get in touch and we'll put together a package that works for you.
              </p>
            </div>

            <div className="flex-shrink-0">
              <a
                href="mailto:hello@gpstech.co.za?subject=TripData Vouchers"
                className="btn btn-gradient gap-2 whitespace-nowrap"
              >
                <Mail size={18} />
                Email us
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Vouchers;